import { PropertyHelper } from '@/helpers/property.helper';
import { DictionaryCacheService } from '@/services/dictionary-cache.service';
import { AttributeValue } from '@/views/property/condo-management/condo-management.component';
import { NgFor, NgIf } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import {
  Attribute,
  AttributeGroup,
  AttributeType,
  PropertiesService,
  PropertyChildrenTypeFilter,
  PropertyType
} from '@eemo/api-client';
import {
  AttributeValueDialogComponent,
  AttributeValueResult
} from '../attribute-value-dialog/attribute-value-dialog.component';
import { IRealProperty } from '../property-list/property-list.component';

import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

export interface FilterPropertyListData {
  name?: string;
  parentId?: string;
  types?: PropertyType[];
  attributeCodes?: string[];
  attributeValues?: string[];
  childrenType?: PropertyChildrenTypeFilter;
  showArchived?: boolean;
}

export interface SelectValue {
  attr: Attribute;
  value: string;
}

@Component({
  selector: 'app-filters-property-list',
  templateUrl: './filters-property-list.component.html',
  styleUrls: ['./filters-property-list.component.scss'],
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatButtonModule,
    MatMenuModule,
    NgFor,
    MatCheckboxModule,
    NgIf,
    MatChipsModule,
    MatSlideToggleModule
  ]
})
export class FiltersPropertyListComponent implements OnChanges {
  @Input() list: IRealProperty[] = [];
  @Input() parentId: string;
  @Input() showPropertyFilter = false;
  @Input() showMoreDetail = false;
  @Input() showChildrenBtn = false;
  @Input() defaultChildrenType = PropertyChildrenTypeFilter.BOUND;
  @Output() filterData = new EventEmitter<FilterPropertyListData>();
  @Output() displayedFullColumns = new EventEmitter<boolean>();

  readonly DELAY_ON_SEARCH = 500;

  data: FilterPropertyListData = {};
  private timerSearch: any;
  displayChildren = false;
  displayFullColumns = false;

  availablePropTypes: PropertyType[];
  filterAttributesValue: { [key: string]: AttributeValue } = {};
  filterAttributesCode = [];

  globalAvailablePropTypes: PropertyType[] = [];
  filterDisplayAttributeValues: { [key: string]: AttributeValue[] } = {};
  filterDisplayAttributes: { [key: string]: Attribute[] } = {};
  filterDisplayAttributeGroups: AttributeGroup[];

  constructor(
    public propHelper: PropertyHelper,
    private dialog: MatDialog,
    private propertyService: PropertiesService,
    public dictCacheService: DictionaryCacheService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['list']) {
      if (changes['list'].firstChange) {
        this.initData();
      }
      this.onNewList();
    }
  }

  initData() {
    this.data = {
      attributeCodes: [],
      attributeValues: [],
      name: null,
      parentId: null,
      types: [],
      childrenType: this.defaultChildrenType
    };
  }

  emitData() {
    this.filterData.next({ ...this.data });
  }

  applyFilter(val: string) {
    if (!val) {
      val = null;
    }
    this.data.name = val;
    if (val != null && val.length < 3) {
      // start search at 3 characters
      return;
    }
    clearTimeout(this.timerSearch);
    this.timerSearch = setTimeout(() => {
      this.emitData();
    }, this.DELAY_ON_SEARCH);
  }

  filterPropertyType(type: PropertyType, checked: boolean) {
    if (checked) {
      this.data.types.push(type);
    } else {
      this.data.types.splice(this.data.types.indexOf(type), 1);
    }
    this.emitData();
  }

  addFilterAttributeValueDialog(attr: Attribute) {
    // prevents to open multiple dialog
    if (this.dialog.openDialogs.length === 0) {
      const dialogRef = this.dialog.open(AttributeValueDialogComponent, {
        // width: '25rem',
        data: {
          attrCode: attr.code,
          attrDisplayName: this.dictCacheService.getAttrDisplayNameFromCode(attr.code)
        }
      });
      dialogRef.afterClosed().subscribe((result: AttributeValueResult) => {
        if (result) {
          this.filterAttributesValue[result.attrCode] = { name: result.attrValue, value: result.attrValue };
          this.setDataAttrs();
          this.emitData();
        }
      });
    }
  }

  addFilterAttributeValue(attr: Attribute, value: string) {
    this.filterAttributesValue[attr.code] = {
      name: attr.availableValues
        ? attr.availableValues
            .filter((a) => a.code === value)
            .map((a) => a.display.name)
            .pop()
        : value,
      value
    };
    this.setDataAttrs();
    this.emitData();
  }

  resetTypeValueFilter(type: PropertyType) {
    this.data.types.splice(this.data.types.indexOf(type), 1);
    this.emitData();
  }

  resetAttributeValueFilter(attrCode: string) {
    delete this.filterAttributesValue[attrCode];
    this.setDataAttrs();
    this.emitData();
  }

  setDataAttrs() {
    this.filterAttributesCode = Object.keys(this.filterAttributesValue);
    this.data.attributeCodes = Object.keys(this.filterAttributesValue);
    this.data.attributeValues = Object.values(this.filterAttributesValue).map((attrValue) => attrValue.value);
  }

  onNewList() {
    this.loadAllPropertiesData();

    this.availablePropTypes = [];
    this.list.forEach((element) => {
      if (!this.availablePropTypes.includes(element.type)) {
        this.availablePropTypes.push(element.type);
      }
    });

    this.filterDisplayAttributes = {};
    this.filterDisplayAttributeValues = {};
    this.filterDisplayAttributeGroups = this.dictCacheService.getAttributeGroupsForCategory('BUILDING');
    this.filterDisplayAttributeGroups.forEach((group) => {
      const attrs = this.dictCacheService.getAttributesForGroupAndPropertyTypes(group.code, this.availablePropTypes);
      if (attrs.length > 0) {
        this.filterDisplayAttributes[group.code] = attrs;
      } else {
        this.filterDisplayAttributeGroups.splice(this.filterDisplayAttributeGroups.indexOf(group), 1);
      }
      attrs.forEach((attr) => {
        if (attr.type === AttributeType.BOOLEAN) {
          this.filterDisplayAttributeValues[attr.code] = [
            { name: 'Oui', value: 'true' },
            { name: 'Non', value: 'false' }
          ];
        } else if (attr.type === AttributeType.ENUM && attr.availableValues) {
          this.filterDisplayAttributeValues[attr.code] = [];
          attr.availableValues.forEach((value) =>
            this.filterDisplayAttributeValues[attr.code].push({ name: value.display.name, value: value.code })
          );
        }
      });
    });
  }

  toggleDisplayChildren() {
    this.displayChildren = !this.displayChildren;
    this.data.childrenType =
      this.data.childrenType === PropertyChildrenTypeFilter.ALL
        ? PropertyChildrenTypeFilter.BOUND
        : PropertyChildrenTypeFilter.ALL;
    this.emitData();
  }

  toggleDisplayColumns() {
    this.displayFullColumns = !this.displayFullColumns;
    this.displayedFullColumns.next(this.displayFullColumns);
  }

  // ---------------------------------------- //
  //                   APIS                   //
  // ---------------------------------------- //
  loadAllPropertiesData() {
    this.propertyService.getRealPropertyLocationSummaryList(this.parentId).subscribe((data) => {
      const globalAvailablePropTypes = [];
      data.forEach((element) => {
        // global count
        if (!globalAvailablePropTypes.includes(element.type)) {
          globalAvailablePropTypes.push(element.type);
        }
      });

      this.globalAvailablePropTypes.sort(PropertyHelper.sortPropertyTypeList);
      this.globalAvailablePropTypes = globalAvailablePropTypes;
    });
  }
}
